/**
 * Fetches the full OncoEMR task thread including queued outbound replies.
 * @module fetchOncoemrTaskMessages
 */

import type {
  FetchOncoemrTaskMessagesParams,
  OncoemrTaskMessagesResponse,
} from "../../data-model/oncoemrTaskMessagesModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";

/**
 * Builds the URL for `GET /oncoemr-tasks/{task_row_id}/messages`.
 *
 * @param taskRowId - Primary key of the `tasks` row (`tasks.id`).
 * @param params - Must include `org_id` for tenancy scoping.
 * @returns Absolute request URL with query string.
 */
export const buildOncoemrTaskMessagesUrl = (
  taskRowId: string,
  params: FetchOncoemrTaskMessagesParams,
): string => {
  const searchParams = new URLSearchParams({
    org_id: params.org_id,
  });
  const base = `${API_ENDPOINTS.ONCOEMR_TASKS}/${encodeURIComponent(taskRowId)}/messages`;
  return `${base}?${searchParams.toString()}`;
};

/**
 * Loads messages and PDF links for one task row, merging inbox history with outbox state.
 *
 * @param taskRowId - `tasks.id` for the selected list row.
 * @param params - Organization scope (`org_id`).
 * @param signal - Optional abort signal.
 * @returns Thread payload: task header, chronological messages, pdf_links.
 * @throws Error when the HTTP response is not OK or JSON cannot be parsed.
 */
export const fetchOncoemrTaskMessages = async (
  taskRowId: string,
  params: FetchOncoemrTaskMessagesParams,
  signal?: AbortSignal,
): Promise<OncoemrTaskMessagesResponse> => {
  const url = buildOncoemrTaskMessagesUrl(taskRowId, params);
  const bearerToken = await getBearerToken();

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
    ...(signal && { signal }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch OncoEMR task messages (${response.status} ${response.statusText})`,
    );
  }

  return (await response.json()) as OncoemrTaskMessagesResponse;
};
